import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import { ICreditCard } from '../../cart-bridger-pay/interfaces';
import InfoIcon from '../../../../ui/svg/common/info';
import Preloader from '../../../../ui/svg/common/preloader';
import {
    getSavedCreditCardsIcon,
    getPaymentMethod,
    UPDATE_SAVED_CREDIT_CARDS_EVENT
} from '../domain/saved-credit-cards-provider';
import { IFormControl } from '../../../../ui/forms/interfaces';
import { removeSavedCreditCard } from '../../cart-bridger-pay/data/data-service';
import { sendGAEvent } from '../../../../domain/marketing/gtm-provider';

/**
 * SAVED CREDIT CARD -------------------------------------
 * This component is shared in multiple shopping carts like:
 * - main cart
 * - change subscription payment method
 * - my credit cards in account
 * All params should be passed as props without the Redux.
 */

export const selectedStyles = {
    backgroundColor: '#ecf5ff',
    border: 'solid 0.7px #3452ff',
};

interface ISavedCreditCardSharedProps {
    savedCard: ICreditCard;
    isSelected: boolean;

    cvvControl?: IFormControl;
    cvvChanged?: (evt: ChangeEvent<HTMLInputElement>) => void;

    showCvvInfoPopup: () => void;
    selectSavedCard: (savedCard: ICreditCard) => void;
    showPopup: () => void;

    hideRemoveButton?: boolean;
    showEditButton?: boolean;
    onEditClick?: (savedCard: ICreditCard) => void;
}

const SavedCreditCardShared = (props: ISavedCreditCardSharedProps) => {

    const {
        savedCard, isSelected,
        showCvvInfoPopup, selectSavedCard, showPopup,
    } = props;

    const [ loading, setLoading ] = useState(false);

    const removeSavedCreditCardHandler = async () => {
        if(loading) return;

        setLoading(true);
        const response = await removeSavedCreditCard(savedCard.token);
        setLoading(false);

        if(!response ||
            response.statusCode !== 200 ||
            !response.data.isValid ||
            !response.data.relatedData) return;

        const relatedData = response.data.relatedData;
        if(relatedData.isSavedCreditCardInUse){
            showPopup();
            return;
        }

        if(relatedData.savedCreditCards){
            document.dispatchEvent(new CustomEvent(UPDATE_SAVED_CREDIT_CARDS_EVENT, {
                detail: relatedData.savedCreditCards,
            }));
        }
    };

    const handleCardClick = () => {
        selectSavedCard(savedCard);
    };

    const onEditClick = () => {
        sendGAEvent('Click', 'Edit credit card info');

        if(!props.onEditClick ) return;
        props.onEditClick(savedCard);
    };

    const onKeyUp = (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            evt.preventDefault();
            handleCardClick();
        }
    };

    return (
        <div key={ savedCard.token }
             tabIndex={ 0 }
             className="border rounded bg-gray-100 flex px-2 py-3 mb-3 cursor-pointer"
             data-qa="cart-step3-saved-card-box"
             style={ isSelected ? selectedStyles : undefined }
             onKeyUp={ onKeyUp }
             onClick={ handleCardClick }>

            <div className="w-4/12 flex flex-col items-center justify-center text-center">
                <div>{getSavedCreditCardsIcon(getPaymentMethod(savedCard.card?.brand || ''), isSelected)}</div>

                <div className="flex items-center justify-center text-tiny mt-2">
                    {
                        !loading && !props.hideRemoveButton &&
                        <button
                            type="button"
                            className="pointer text-tiny text-blue-200 font-bold"
                            onClick={removeSavedCreditCardHandler}>Remove</button>
                    }

                    {
                        !loading && props.showEditButton &&
                        <button
                            type="button"
                            className="pointer text-blue-200 font-bold ml-4"
                            onClick={ onEditClick }>Edit</button>
                    }
                </div>

                {loading && <Preloader width="15px" height="15px" color="#4f4f4f" classes="mt-2"/>}
            </div>

            <div className="w-8/12 text-gray-500 pl-4 border-l flex flex-col justify-center">
                <div>XXXX-XXXX-XXXX-{savedCard.card?.last_4_digits || ''}</div>
                <div>{savedCard.card?.holder_name || ''}</div>
                <div>{savedCard.card?.expire_month || ''}/{savedCard.card?.expire_year || ''}</div>

                {
                    isSelected &&
                    props.cvvControl &&
                    props.cvvChanged &&
                    !savedCard.encrypted_cvv &&
                    <div className="flex items-center">
                        <input
                            id="cvv-tb"
                            value={props.cvvControl.value}
                            onInput={props.cvvChanged}
                            type="text"
                            name="cvv"
                            placeholder="Enter CVV"
                            maxLength={ 4 }
                            title="Enter CVV"
                            size={ 4 }
                            className={ `border text-sm px-1 mr-1 rounded ${ props.cvvControl.isValid ? '' : 'border-red' }` }
                            style={{
                                width: '72px',
                            }}
                        />
                        <button
                            type="button"
                            title="What is CVV?"
                            className="text-sm text-gray-500 flex items-center"
                            onClick={ showCvvInfoPopup }>
                            <InfoIcon width="15px" color="#777" />
                        </button>
                    </div>
                }
            </div>
        </div>
    )
};

export default SavedCreditCardShared;